import { isEqual, noop, omit } from 'lodash-es'
import { format, isBefore, isValid, startOfToday } from 'date-fns'
import { filterAvailableTimes } from '@myfunnow/web-core/time-filter'

import { useAppStore } from '@/store/app'
// import { useRegionStore } from '@/store/region'
import { useCurrencyStore } from '@/store/currency'

import { DEFAULT_FILTER_STATE, HOME_PAGE_FILTER_LAYOUT_TYPE_LIST, DEFAULT_FILTER_MAX_COUNT, DEFAULT_MAX_HEADCOUNT } from '@/constants/filters'
import { AsyncDataKeys } from '@/constants/async-data-keys'
import { QueryStringKeys } from '@/constants/commons'

import type { FilterComponentState, FilterLayout, FormattedPriceValue, QueryStringFilterState } from '@/types/filters'
import { FilterTypeEnum, FilterUiTypeEnum, CurrencyEnum } from '@/types/filters'
import { FilterTimeOptionsEnum, SortRuleEnum } from '@/types/product'
import type { EatigoFilterFilterRes, SfilterComponent, SfilterFilterTag, SfilterTag } from '@/types/api'

type FilterComponentName = Exclude<SfilterComponent['name'], undefined> | 'discount'

export function useSearchFilter() {
  const nuxtApp = useNuxtApp()
  const { t, locale } = useI18n()
  const route = useRoute()
  const router = useLocaleRouter()

  const appStore = useAppStore()
  // const regionStore = useRegionStore()
  const currencyStore = useCurrencyStore()

  const { tagId, categoryId } = useCategory()
  const { regionId } = useRegion()

  const isProductListPage = computed(() => checkIfProductListPage(route.name))
  // const cuisineFilterInfo = computed(() => regionStore.regionInfo?.cuisine_filter ?? {})

  const getFilterString = () => {
    // const isCuisineFilterSelected = filterState.value[FilterTypeEnum.CUISINE_TAGS].length > 0 && !!cuisineFilterInfo.value.id
    const trimmedObj = trimObject({
      ...baseFilterParams.value,
      ...restFilterTags.value.reduce(
        (acc, cur) => {
          const key = `${cur.id}`
          if (key) acc[key] = filterState.value[key] as number[]
          return acc
        },
        {} as Record<string, number[]>
      ),
    }) as QueryStringFilterState

    return Object.keys(trimmedObj).length ? JSON.stringify(trimmedObj) : null
  }

  // private util functions
  function _getFilterOptionsByName(name: FilterComponentName) {
    return filterComponentMap.value[name]?.options
  }

  function _createOptionTagByName(name: FilterComponentName) {
    return _getFilterOptionsByName(name)?.map(option => ({ id: Number(option.value), name: option.title })) ?? []
  }

  const _updateFilterState = <FilterTypeEnum extends keyof FilterComponentState>(
    key: FilterTypeEnum,
    value: FilterComponentState[FilterTypeEnum]
  ) => {
    if (key in filterState.value && !isEqual(filterState.value[key], value)) {
      filterState.value[key] = value
    }
  }

  const _getQueryStringFilterState = (): QueryStringFilterState => {
    if (!isProductListPage.value) {
      return {}
    }

    function genOptionValueSetByName(name: FilterComponentName) {
      return new Set(_getFilterOptionsByName(name)?.map(option => option.value))
    }

    const queryFilter = getFilterQueryString(route.query)
    const { restFilter, ...others } = queryFilter
    const minPrice = others[FilterTypeEnum.MIN_PRICE]
    const maxPrice = others[FilterTypeEnum.MAX_PRICE]
    const minPriceIndex = priceOptions.value.findIndex(opt => Number(opt.min) === minPrice)
    const maxPriceIndex = priceOptions.value.findIndex(opt => Number(opt.max) === maxPrice)
    const price: FormattedPriceValue['values'] = [minPriceIndex === -1 ? null : minPriceIndex, maxPriceIndex === -1 ? null : maxPriceIndex]
    const rating = others[FilterTypeEnum.RATING]
    const headcountSet = genOptionValueSetByName('headcount')
    const ratingSet = genOptionValueSetByName('rating')

    const limit = (() => {
      const _limit = others[FilterTypeEnum.LIMIT]

      if (!_limit || _limit <= 0) {
        return 0
      }
      if (headcountSet.size > 0) {
        return headcountSet.has(`${_limit}`) ? _limit : 0
      }

      return _limit <= DEFAULT_MAX_HEADCOUNT ? _limit : 0
    })()
    const date = others[FilterTypeEnum.START_TIME] ?? null
    const filterTimes = (() => {
      const times = Array.isArray(others[FilterTypeEnum.FILTER_TIMES]) ? others[FilterTypeEnum.FILTER_TIMES] : []

      return date ? filterAvailableTimes(times, date) : times
    })()
    const unnecessaryKeys = [
      FilterTypeEnum.MIN_PRICE,
      FilterTypeEnum.MAX_PRICE,
      FilterTypeEnum.START_TIME,
      FilterTypeEnum.END_TIME,
      FilterTypeEnum.FILTER_TIME,
    ]

    return {
      ...omit(others, unnecessaryKeys),
      ...restFilter,
      [FilterTypeEnum.PRICE]: price,
      [FilterTypeEnum.DATE]: date,
      [FilterTypeEnum.NOW]: Number(others[FilterTypeEnum.FILTER_TIME] === FilterTimeOptionsEnum.NOW),
      [FilterTypeEnum.LIMIT]: limit,
      [FilterTypeEnum.RATING]: ratingSet.has(`${rating}`) ? rating : 0,
      [FilterTypeEnum.FILTER_TIMES]: filterTimes,
    }
  }

  const _updateQueryString = async () => {
    if (!isProductListPage.value) {
      return
    }

    const queries = cloneDeep(route.query)
    const filterString = getFilterString()

    if (filterString) {
      queries[QueryStringKeys.SEARCH_FILTER] = filterString
    } else {
      delete queries[QueryStringKeys.SEARCH_FILTER]
    }
    delete queries[QueryStringKeys.LIST_PAGE]

    await router.push({ query: queries })
  }

  const filterOptionsKey = useAsyncDataKey(AsyncDataKeys.FILTER_OPTIONS, [regionId.value, tagId.value, categoryId.value])
  const { data: filterOptions } = useNuxtData<EatigoFilterFilterRes>(filterOptionsKey)

  // data sort options
  const filterSortbyOptions = computed(() => [
    ...(appStore.isLocationAcquired
      ? [
          {
            label: t('sorting.distance'),
            value: SortRuleEnum.DISTANCE,
          },
        ]
      : []),
    {
      label: t('sorting.recommend'),
      value: SortRuleEnum.POPULAR,
    },
    {
      label: t('sorting.price_asc'),
      value: SortRuleEnum.PRICE_LOW,
    },
    {
      label: t('sorting.price_desc'),
      value: SortRuleEnum.PRICE_HIGH,
    },
  ])

  const filterComponentMap = computed(() => convertArrayToKeyedObject(filterOptions.value?.components ?? [], 'name'))
  const filterMaxCount = computed(() => filterOptions.value?.max_count ?? DEFAULT_FILTER_MAX_COUNT)
  const currencyId = computed(() => filterOptions.value?.currency_id ?? CurrencyEnum.HKD)
  const currencyCode = computed(() => currencyStore.currencyMapById[currencyId.value]?.code ?? CurrencyEnum[CurrencyEnum.HKD])

  // price range
  // spec: https://www.figma.com/design/H8XF891oLkmXTTsE1fS0t5/eatigo-WEB?node-id=1961-123352&m=dev
  const priceOptions = computed(() => _getFilterOptionsByName('price') ?? [])
  const priceMarks = computed(() => priceOptions.value.reduce((acc, _cur, index) => ({ ...acc, [index]: index }), {}))
  const priceLabelFormatter = (value: number) => '$'.repeat(value)
  const priceValueFormatter = (values: FormattedPriceValue['values']): FormattedPriceValue => {
    if (priceOptions.value.length === 0) {
      return { values }
    }

    const priceOptionsLen = priceOptions.value.length - 1
    const sortedValues = [values[0] ?? 0, values[1] ?? priceOptionsLen].sort((a, b) => Number(a) - Number(b))
    const [minIndex, maxIndex] = sortedValues
    const minOption = priceOptions.value[minIndex]
    const maxOption = priceOptions.value[maxIndex]
    const minPriceTextWithCurrency = `${currencyCode.value} ${formatNumberWithCommas(minOption.min, locale.value)}`
    const maxPriceTextWithCurrency = `${currencyCode.value} ${formatNumberWithCommas(maxOption.max, locale.value)}`
    const maxPriceText = formatNumberWithCommas(maxOption.max, locale.value)
    const minPrice = minIndex === 0 || !minOption.min ? null : Number(minOption.min)
    const maxPrice = maxIndex === priceOptionsLen || !maxOption.max ? null : Number(maxOption.max)
    const maxOptionTitle = t('filter.price.more_than', { currency: currencyCode.value, amount: formatNumberWithCommas(maxOption.min, locale.value) })
    const label = (() => {
      if (minIndex === priceOptionsLen && maxIndex === priceOptionsLen) {
        return maxOptionTitle
      }

      return maxIndex === priceOptionsLen ? `${minPriceTextWithCurrency} - ${maxOptionTitle}` : `${minPriceTextWithCurrency} - ${maxPriceText}`
    })()
    const content = (() => {
      if (minIndex === priceOptionsLen && maxIndex === priceOptionsLen) {
        return maxOptionTitle
      }

      return maxIndex === priceOptionsLen
        ? `${minPriceTextWithCurrency} - ${maxOptionTitle}`
        : `${minPriceTextWithCurrency} - ${maxPriceTextWithCurrency}`
    })()

    return { values, label, content, minPrice, maxPrice }
  }

  const restFilterTags = computed(() => filterOptions.value?.filter_tags?.filter(tag => typeof tag.id !== 'undefined') ?? [])

  const headcountTags = computed<SfilterTag[]>(() => {
    const optionsFromApi = _createOptionTagByName('headcount')

    if (isProductListPage.value && optionsFromApi.length > 0) {
      return optionsFromApi
    }

    const tags = Array.from({ length: DEFAULT_MAX_HEADCOUNT }).map((_, index) => ({
      id: index + 1,
      name: `${index + 1}`,
    }))

    return [{ id: 0, name: t('filter.people.unlimited') }].concat(tags)
  })

  // const cuisineTags = computed<SfilterTag[]>(() => cuisineFilterInfo.value.tags ?? [])

  const filterItemOption = computed<Record<string, any>>(() => {
    const restFilterOptions = restFilterTags.value?.reduce(
      (acc, c) => {
        const key = `${c.id}`
        acc[key] = {
          options: c,
        }
        return acc
      },
      {} as Record<string, { options: SfilterFilterTag }>
    )
    return {
      [FilterTypeEnum.SORTBY]: {
        options: filterSortbyOptions.value,
      },
      [FilterTypeEnum.DATE]: {
        dayMode: filterOptions.value?.day_mode,
      },
      [FilterTypeEnum.FILTER_TIMES]: {
        selectedDate: filterState.value[FilterTypeEnum.DATE],
      },
      [FilterTypeEnum.LIMIT]: {
        options: {
          tags: headcountTags.value,
        },
        class: 'md:h-86',
      },
      [FilterTypeEnum.PRICE]: {
        currencyId: currencyId.value,
        step: 'mark',
        min: 0,
        max: priceOptions.value.length - 1,
        marks: priceMarks.value,
        labelFormatter: priceLabelFormatter,
        valueFormatter: priceValueFormatter,
      },
      [FilterTypeEnum.DISCOUNT]: {
        options: {
          tags: _createOptionTagByName('discount'),
        },
      },
      [FilterTypeEnum.RATING]: {
        options: {
          tags: _createOptionTagByName('rating'),
        },
      },
      // [FilterTypeEnum.CUISINE_TAGS]: {
      //   options: {
      //     tags: cuisineTags.value,
      //   },
      // },
      ...restFilterOptions,
    }
  })

  const filterLayoutMap = computed<Record<string, FilterLayout>>(() => ({
    [FilterTypeEnum.NOW]: {
      name: FilterTypeEnum.NOW,
      label: t('filter.now'),
      icon: 'now',
      clearable: true,
    },
    [FilterTypeEnum.DATE]: {
      name: FilterTypeEnum.DATE,
      label: t('filter.date'),
      icon: 'calendar',
      type: FilterUiTypeEnum.DATE_RANGE,
      clearable: true,
    },
    [FilterTypeEnum.FILTER_TIMES]: {
      name: FilterTypeEnum.FILTER_TIMES,
      label: t('filter.time.title'),
      icon: 'time',
      type: FilterUiTypeEnum.TIME_PICKER,
      clearable: true,
    },
    [FilterTypeEnum.LIMIT]: {
      name: FilterTypeEnum.LIMIT,
      label: t('filter.people'),
      icon: 'person',
      type: appStore.isMobileScreen ? FilterUiTypeEnum.RADIO_BUTTON : FilterUiTypeEnum.RADIO,
      clearable: true,
    },
    [FilterTypeEnum.PRICE]: {
      name: FilterTypeEnum.PRICE,
      label: t('filter.price'),
      icon: 'money',
      type: FilterUiTypeEnum.MARK_RANGE,
      clearable: true,
    },
    [FilterTypeEnum.DISCOUNT]: {
      name: FilterTypeEnum.DISCOUNT,
      label: t('filter.discount'),
      icon: 'discount',
      type: FilterUiTypeEnum.RADIO,
      clearable: true,
    },
    [FilterTypeEnum.RATING]: {
      name: FilterTypeEnum.RATING,
      label: t('filter.rating'),
      icon: 'star',
      type: FilterUiTypeEnum.RADIO,
      clearable: true,
    },
    // [FilterTypeEnum.CUISINE_TAGS]: {
    //   name: FilterTypeEnum.CUISINE_TAGS,
    //   label: cuisineFilterInfo.value?.title ?? t('home.cuisine.title'),
    //   icon: 'cuisine',
    //   type: FilterUiTypeEnum.CHECK_BOX,
    //   clearable: true,
    // },
  }))

  const filterLayout = computed<FilterLayout[]>(() => {
    const listPageFilterLayoutTypeList = [
      FilterTypeEnum.NOW,
      FilterTypeEnum.DATE,
      FilterTypeEnum.FILTER_TIMES,
      FilterTypeEnum.LIMIT,
      !!_getFilterOptionsByName('price') && FilterTypeEnum.PRICE,
      !!_getFilterOptionsByName('discount') && FilterTypeEnum.DISCOUNT,
      !!_getFilterOptionsByName('rating') && FilterTypeEnum.RATING,
    ].filter(Boolean) as FilterTypeEnum[]
    const resetFilterTagsLayout = restFilterTags.value?.map(i => ({
      name: `${i.id ?? ''}`,
      label: i.title,
      icon: undefined,
      type: FilterUiTypeEnum.CHECK_BOX,
      clearable: true,
    }))
    const layoutTypeList = isProductListPage.value ? listPageFilterLayoutTypeList : HOME_PAGE_FILTER_LAYOUT_TYPE_LIST
    const layoutList = layoutTypeList.map(type => filterLayoutMap.value[type])

    return isProductListPage.value ? layoutList.concat(resetFilterTagsLayout) : layoutList
  })

  // state management
  const baseFilterState: FilterComponentState = {
    ...DEFAULT_FILTER_STATE,
    ...(appStore.isLocationAcquired && !nuxtApp.isHydrating ? { [FilterTypeEnum.SORTBY]: SortRuleEnum.DISTANCE } : {}),
    ...restFilterTags.value.reduce(
      (acc, cur) => {
        if (cur.id) acc[cur.id] = []
        return acc
      },
      {} as Record<string, number[]>
    ),
  }

  const filterState = ref<FilterComponentState>({
    ...cloneDeep(baseFilterState),
    ..._getQueryStringFilterState(),
  })

  const lastSubmitFilterState = ref<FilterComponentState>(cloneDeep(filterState.value))

  const baseFilterParams = computed(() => {
    const toDate = filterState.value[FilterTypeEnum.DATE] ? new Date(filterState.value[FilterTypeEnum.DATE]) : null
    const date = toDate && isValid(toDate) && !isBefore(toDate, startOfToday()) ? format(toDate, 'yyyy-MM-dd') : null
    const formattedPriceValue = priceValueFormatter(filterState.value[FilterTypeEnum.PRICE])
    // 清理在指定日期下已不可選的時間
    const availableFilterTimes = date
      ? filterAvailableTimes(filterState.value[FilterTypeEnum.FILTER_TIMES], date)
      : filterState.value[FilterTypeEnum.FILTER_TIMES]

    return {
      [FilterTypeEnum.SORTBY]: filterState.value[FilterTypeEnum.SORTBY],
      ...(filterState.value[FilterTypeEnum.NOW] && { [FilterTypeEnum.FILTER_TIME]: FilterTimeOptionsEnum.NOW }),
      ...(filterState.value[FilterTypeEnum.LIMIT] > 0 && { [FilterTypeEnum.LIMIT]: filterState.value[FilterTypeEnum.LIMIT] }),
      ...(filterState.value[FilterTypeEnum.DISCOUNT] > 0 && { [FilterTypeEnum.DISCOUNT]: filterState.value[FilterTypeEnum.DISCOUNT] }),
      ...(filterState.value[FilterTypeEnum.RATING] > 0 && { [FilterTypeEnum.RATING]: filterState.value[FilterTypeEnum.RATING] }),
      ...(date && { [FilterTypeEnum.START_TIME]: date, [FilterTypeEnum.END_TIME]: date }),
      ...(availableFilterTimes.length > 0 && { [FilterTypeEnum.FILTER_TIMES]: availableFilterTimes }),
      ...(formattedPriceValue.minPrice && { [FilterTypeEnum.MIN_PRICE]: formattedPriceValue.minPrice }),
      ...(formattedPriceValue.maxPrice && { [FilterTypeEnum.MAX_PRICE]: formattedPriceValue.maxPrice }),
    }
  })

  const filterStateUnWrap = computed(() => ({
    ...baseFilterParams.value,
    filter_tags: trimNestedArray([
      ...restFilterTags.value.map(t => {
        const key = `${t.id}`
        return (filterState.value[key] as number[]) ?? []
      }),
      // filterState.value[FilterTypeEnum.CUISINE_TAGS],
    ]),
  }))

  const isNonSubmitButtonChanged = computed(() =>
    [FilterTypeEnum.SORTBY, FilterTypeEnum.NOW].some(name => filterState.value[name] !== lastSubmitFilterState.value[name])
  )

  function saveLastSubmitFilterState() {
    lastSubmitFilterState.value = cloneDeep(filterState.value)
  }

  function handleQueryFilterUpdated(cb: () => void = noop) {
    const newFilterState = { ...cloneDeep(baseFilterState), ..._getQueryStringFilterState() }

    if (!isEqual(newFilterState, filterState.value)) {
      filterState.value = newFilterState
      saveLastSubmitFilterState()
      cb()
    }
  }

  const clear = (key: string) => _updateFilterState(key, cloneDeep(baseFilterState[key]))
  const reset = (key: string) => _updateFilterState(key, cloneDeep(lastSubmitFilterState.value[key]))
  const saveLastSubmitted = async () => {
    await _updateQueryString()
    saveLastSubmitFilterState()
  }

  return {
    baseFilterState: readonly(baseFilterState),
    filterItemOption,
    filterLayout,

    filterState,
    lastSubmitFilterState: readonly(lastSubmitFilterState),
    filterStateUnWrap,
    isNonSubmitButtonChanged,
    filterMaxCount,

    getFilterString,
    handleQueryFilterUpdated,
    reset,
    clear,
    saveLastSubmitted,
  }
}
